/**
 * Asking a job's transcripts a question.
 *
 * Jonathon, 2026-09-07, after asking for the Recorders page to go: "I just need
 * a place on the customer's job to upload a transcript so that way we can go
 * back to it and ask questions about the inspection/interaction with the
 * customer."
 *
 * The uploading half already existed — Job → Conversations has taken a pasted
 * transcript since ADR 0064. The asking half is this. A rep types "what did we
 * promise them about the gutters?" and gets an answer read out of what was
 * actually said at that house.
 *
 * **The answer is quoted from the transcripts or it is refused.** That is the
 * whole design constraint and the reason this module exists rather than the
 * component calling a model directly. What comes back is read to a customer, or
 * argued with a customer, and a model that fills a gap plausibly is worse here
 * than one that says it does not know: "you told them the drip edge was
 * included" is a sentence somebody will act on. So the prompt is built server
 * side, from transcripts only, and an answer with no support in them is meant
 * to say so.
 *
 * This lives in core rather than in the component (CLAUDE.md rule 5): what may
 * be asked, which transcripts are sent, and what of the answer is allowed on
 * screen are business rules, and the same rules are transcribed into
 * `supabase/functions/conversation-ask/request.ts` for the function that holds
 * the key.
 *
 * ADR 0113.
 */

/**
 * The bounds an asking lives inside.
 *
 * Transcribed into `supabase/functions/conversation-ask/request.ts`, which
 * cannot import the workspace; `supabase/test/conversation-ask-match.test.ts`
 * binds the two so they cannot drift.
 */
export const ASK_LIMITS = {
  /** Shorter than this is not a question. */
  questionMin: 3,
  /** A question, not an essay. */
  questionMax: 500,
  /**
   * Characters of transcript sent in one asking.
   *
   * A visit runs to a few thousand characters, so this holds several of them.
   * It is a budget rather than a count because the point is the size of the
   * prompt, and one long inspection should not be cut in half to make room for
   * four short ones it has nothing to do with.
   */
  transcriptBudget: 60_000,
  /**
   * The smallest piece of a transcript worth sending.
   *
   * When the budget runs low the last transcript is cut, and a cut of forty
   * characters is not an excerpt — it is noise with a heading on it, and the
   * model has to be told to ignore something it cannot judge. Below this the
   * transcript is left out instead.
   */
  excerptMin: 500,
  /** What is kept of the model's reply. */
  answerMax: 4000,
} as const;

/** A transcript, as this module needs to see one. */
export interface AskableConversation {
  id: string;
  name?: string | null;
  started_at?: string | null;
  transcript_text?: string | null;
}

/** Why this question cannot be asked, if it cannot. */
export function askBlocker(
  question: string,
  conversations: readonly AskableConversation[],
): string | null {
  const trimmed = question.trim();
  if (trimmed.length < ASK_LIMITS.questionMin) {
    return "Type a question first.";
  }
  if (trimmed.length > ASK_LIMITS.questionMax) {
    return "That is longer than a question. Ask one thing at a time.";
  }
  if (askableTranscripts(conversations).length === 0) {
    return "There is no transcript on this job yet. Add one above, then ask.";
  }
  return null;
}

/** The conversations that have words in them. The rest cannot answer anything. */
export function askableTranscripts(
  conversations: readonly AskableConversation[],
): AskableConversation[] {
  return conversations.filter(
    (conversation) => (conversation.transcript_text ?? "").trim() !== "",
  );
}

/** One transcript, chosen and trimmed, on its way into a prompt. */
export interface AskExcerpt {
  id: string;
  label: string;
  text: string;
  /** True when this transcript did not fit whole. */
  trimmed: boolean;
}

/**
 * Which transcripts to send, and in what order.
 *
 * **Newest first when choosing, oldest first when reading.** A job accumulates
 * visits, and if the budget will not hold all of them the recent ones are the
 * ones a question is usually about — but an answer that reasons about what was
 * said needs them in the order they were said, or "then we agreed" means
 * nothing. So the choosing and the ordering are deliberately opposite, and this
 * is the only place that is true.
 *
 * A transcript that does not fit whole is cut rather than dropped, and says so.
 * Half a conversation still answers "was the chimney mentioned"; a conversation
 * that silently vanished answers it wrongly.
 */
export function askExcerpts(
  conversations: readonly AskableConversation[],
  budget: number = ASK_LIMITS.transcriptBudget,
): AskExcerpt[] {
  const withWords = askableTranscripts(conversations);
  const newestFirst = [...withWords].sort(
    (a, b) => startedMs(b.started_at) - startedMs(a.started_at),
  );

  const chosen: AskExcerpt[] = [];
  let left = budget;
  for (const conversation of newestFirst) {
    const text = (conversation.transcript_text ?? "").trim();
    const fits = text.length <= left;
    // Whole, or a piece big enough to be worth reading. Anything less is left
    // out, and once there is no room the rest are too.
    if (!fits && left < Math.min(ASK_LIMITS.excerptMin, text.length)) break;
    chosen.push({
      id: conversation.id,
      label: excerptLabel(conversation),
      text: fits ? text : text.slice(0, left),
      trimmed: !fits,
    });
    left -= fits ? text.length : left;
  }

  const order = new Map(withWords.map((c, index) => [c.id, index]));
  return chosen.sort((a, b) => {
    const left = conversationBy(withWords, a.id);
    const right = conversationBy(withWords, b.id);
    const byDate = startedMs(left?.started_at) - startedMs(right?.started_at);
    if (byDate !== 0) return byDate;
    return (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0);
  });
}

/** How a transcript is named to the model, and in the answer. */
export function excerptLabel(conversation: {
  name?: string | null;
  started_at?: string | null;
}): string {
  const name = (conversation.name ?? "").trim();
  const when = (conversation.started_at ?? "").slice(0, 10);
  if (name && when) return `${name} (${when})`;
  return name || when || "Recording";
}

/**
 * What is kept of the model's reply.
 *
 * Trimmed and capped, and nothing else — the model is asked for prose a person
 * reads, so there is no shape to validate. An empty reply is an error rather
 * than an empty answer on screen: "it had nothing to say" and "it never
 * answered" look identical in a box and mean different things.
 */
export function readAskAnswer(raw: unknown): string {
  const text = typeof raw === "string" ? raw.trim() : "";
  if (text === "") {
    throw new Error(
      "The AI did not answer that. Try asking it a different way.",
    );
  }
  return text.slice(0, ASK_LIMITS.answerMax);
}

/** Milliseconds, or zero for a date nothing can be made of. */
function startedMs(value: string | null | undefined): number {
  if (!value) return 0;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? 0 : ms;
}

function conversationBy(
  conversations: readonly AskableConversation[],
  id: string,
): AskableConversation | undefined {
  return conversations.find((conversation) => conversation.id === id);
}
